import React, { useState } from 'react'
import backgroundImage from '@assets/image.png'
import { cmsService } from '@services/api'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  Card,
  CardBody,
  CardHeader,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Divider,
  Badge,
  Icon,
  useColorModeValue,
  Checkbox,
  CheckboxGroup,
  Stack,
} from '@chakra-ui/react'
import { 
  Database, 
  Shield, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  Mail,
  Phone,
  Lock,
  User,
  CreditCard,
  MapPin,
  Calendar
} from 'lucide-react'

export default function DeleteDataPage() {
  const [formData, setFormData] = useState({
    email: '',
    phoneNumber: '',
    password: '',
    dataTypes: [] as string[],
    reason: '',
    confirmDeletion: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const toast = useToast()

  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')

  const dataTypes = [
    { id: 'profile', label: 'Profile Information', description: 'Name, email, phone number, profile picture', icon: User },
    { id: 'bookings', label: 'Booking History', description: 'All past and current bookings, trip history', icon: Calendar },
    { id: 'payments', label: 'Payment Information', description: 'Payment methods, transaction history, receipts', icon: CreditCard },
    { id: 'location', label: 'Location Data', description: 'Saved locations, route preferences, GPS data', icon: MapPin },
    { id: 'preferences', label: 'App Preferences', description: 'Settings, notifications, theme preferences', icon: Shield },
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await cmsService.requestDataDeletion({
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        dataTypes: formData.dataTypes,
        reason: formData.reason || undefined
      })
      
      if (response.success) {
        setIsSubmitted(true)
        toast({
          title: 'Data Deletion Request Submitted',
          description: response.message || 'Your selected data will be deleted within 30 days. You will receive a confirmation email.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || error?.message || 'Failed to submit deletion request. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleDataTypeChange = (dataType: string, isChecked: boolean) => {
    if (isChecked) {
      setFormData(prev => ({
        ...prev,
        dataTypes: [...prev.dataTypes, dataType]
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        dataTypes: prev.dataTypes.filter(type => type !== dataType)
      }))
    }
  }

  if (isSubmitted) {
    return (
      <Box
        minH="100vh"
        backgroundImage={`url(${backgroundImage})`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        backgroundAttachment="fixed"
        py={8}
        position="relative"
      >
        {/* Overlay for better readability */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(0, 0, 0, 0.4)"
          zIndex={1}
        />
        <Center position="relative" zIndex={2}>
          <Card maxW="600px" w="full" bg={cardBg} shadow="lg">
            <CardBody p={8}>
              <VStack spacing={6} textAlign="center">
                <Box p={4} bg="green.100" borderRadius="full">
                  <CheckCircle size={48} color="var(--chakra-colors-green-500)" />
                </Box>
                
                <VStack spacing={4}>
                  <Heading size="lg" color="green.600">
                    Data Deletion Request Submitted
                  </Heading>
                  
                  <Text color="gray.600" fontSize="lg">
                    Your data deletion request has been successfully submitted.
                  </Text>
                  
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>30-Day Processing Period</AlertTitle>
                      <AlertDescription>
                        Your selected data will be permanently deleted within 30 days. You will receive updates via email.
                      </AlertDescription>
                    </Box>
                  </Alert>
                  
                  <VStack spacing={3} align="start" w="full">
                    <HStack spacing={3}>
                      <Mail size={20} color="var(--chakra-colors-blue-500)" />
                      <Text>Confirmation email sent to {formData.email}</Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Clock size={20} color="var(--chakra-colors-orange-500)" />
                      <Text>Deletion scheduled for 30 days from now</Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Database size={20} color="var(--chakra-colors-green-500)" />
                      <Text>Selected data types will be permanently removed</Text>
                    </HStack>
                  </VStack>

                  {/* Selected Data Types */}
                  <Card bg="blue.50" border="1px solid" borderColor="blue.200" w="full">
                    <CardBody>
                      <VStack spacing={3} align="start">
                        <Text fontWeight="semibold" color="blue.800">Data to be deleted:</Text>
                        <VStack spacing={2} align="start" w="full">
                          {formData.dataTypes.map(dataTypeId => {
                            const dataType = dataTypes.find(dt => dt.id === dataTypeId)
                            return dataType ? (
                              <HStack key={dataTypeId} spacing={2}>
                                <CheckCircle size={16} color="var(--chakra-colors-green-500)" />
                                <Text fontSize="sm" color="blue.700">{dataType.label}</Text>
                              </HStack>
                            ) : null
                          })}
                        </VStack>
                      </VStack>
                    </CardBody>
                  </Card>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </Center>
      </Box>
    )
  }

  return (
    <Box
      minH="100vh"
      backgroundImage={`url(${backgroundImage})`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      backgroundAttachment="fixed"
      py={8}
      position="relative"
    >
      {/* Overlay for better readability */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0, 0, 0, 0.4)"
        zIndex={1}
      />
      <Center position="relative" zIndex={2}>
        <Card maxW="700px" w="full" bg={cardBg} shadow="lg">
          <CardHeader textAlign="center" pb={4}>
            <VStack spacing={4}>
              <Box p={4} bg="orange.100" borderRadius="full">
                <Database size={48} color="var(--chakra-colors-orange-500)" />
              </Box>
              
              <VStack spacing={2}>
                <Heading size="xl" color="gray.900">
                  Delete Your Data
                </Heading>
                <Text color="gray.600" fontSize="lg">
                  Request deletion of specific data from your Daladala Smart account
                </Text>
              </VStack>
            </VStack>
          </CardHeader>

          <CardBody pt={0}>
            <VStack spacing={6} align="stretch">
              {/* Information Alert */}
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Selective Data Deletion</AlertTitle>
                  <AlertDescription>
                    You can choose which types of data to delete while keeping your account active. This allows you to maintain your account while removing specific data categories.
                  </AlertDescription>
                </Box>
              </Alert>

              {/* Process Information */}
              <Card bg="blue.50" border="1px solid" borderColor="blue.200">
                <CardBody>
                  <VStack spacing={4} align="start">
                    <HStack spacing={3}>
                      <Clock size={20} color="var(--chakra-colors-blue-600)" />
                      <Text fontWeight="semibold" color="blue.800">30-Day Processing Period</Text>
                    </HStack>
                    <Text color="blue.700" fontSize="sm">
                      Your selected data will be scheduled for deletion within 30 days. During this period, you can contact support to cancel the deletion request if needed.
                    </Text>
                  </VStack>
                </CardBody>
              </Card>

              {/* Deletion Form */}
              <form onSubmit={handleSubmit}>
                <VStack spacing={5} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>
                      <HStack spacing={2}>
                        <Mail size={16} />
                        <Text>Email Address</Text>
                      </HStack>
                    </FormLabel>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter your registered email address"
                      size="lg"
                      borderRadius="xl"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>
                      <HStack spacing={2}>
                        <Phone size={16} />
                        <Text>Phone Number</Text>
                      </HStack>
                    </FormLabel>
                    <Input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      placeholder="Enter your registered phone number"
                      size="lg"
                      borderRadius="xl"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>
                      <HStack spacing={2}>
                        <Lock size={16} />
                        <Text>Password</Text>
                      </HStack>
                    </FormLabel>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter your account password"
                      size="lg"
                      borderRadius="xl"
                    />
                  </FormControl>

                  <Divider />

                  {/* Data Types Selection */}
                  <FormControl isRequired>
                    <FormLabel>
                      <HStack spacing={2}>
                        <Database size={16} />
                        <Text>Select Data Types to Delete</Text>
                      </HStack>
                    </FormLabel>
                    <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                      <CardBody>
                        <VStack spacing={4} align="stretch">
                          {dataTypes.map((dataType) => (
                            <Card key={dataType.id} bg="white" border="1px solid" borderColor="gray.200">
                              <CardBody p={4}>
                                <HStack spacing={3} align="start">
                                  <Checkbox
                                    id={`data-type-${dataType.id}`}
                                    isChecked={formData.dataTypes.includes(dataType.id)}
                                    onChange={(e) => handleDataTypeChange(dataType.id, e.target.checked)}
                                    colorScheme="green"
                                    size="lg"
                                  />
                                  <VStack align="start" spacing={1} flex={1}>
                                    <HStack spacing={2}>
                                      <dataType.icon size={18} color="var(--chakra-colors-gray-600)" />
                                      <Text fontWeight="semibold" color="gray.800">
                                        {dataType.label}
                                      </Text>
                                    </HStack>
                                    <Text fontSize="sm" color="gray.600">
                                      {dataType.description}
                                    </Text>
                                  </VStack>
                                </HStack>
                              </CardBody>
                            </Card>
                          ))}
                        </VStack>
                      </CardBody>
                    </Card>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Reason for Data Deletion (Optional)</FormLabel>
                    <Textarea
                      value={formData.reason}
                      onChange={(e) => handleInputChange('reason', e.target.value)}
                      placeholder="Help us understand why you want to delete this data..."
                      size="lg"
                      borderRadius="xl"
                      rows={4}
                    />
                  </FormControl>

                  <Divider />

                  {/* Confirmation Checkbox */}
                  <Card bg="orange.50" border="1px solid" borderColor="orange.200">
                    <CardBody>
                      <HStack spacing={3} align="start">
                        <Checkbox
                          id="confirm-deletion"
                          isChecked={formData.confirmDeletion}
                          onChange={(e) => handleInputChange('confirmDeletion', e.target.checked)}
                          colorScheme="orange"
                          size="lg"
                        />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="semibold" color="orange.800">
                            I understand the consequences
                          </Text>
                          <Text fontSize="sm" color="orange.700">
                            I confirm that I want to permanently delete the selected data types from my account. 
                            This action cannot be undone after the 30-day processing period.
                          </Text>
                        </VStack>
                      </HStack>
                    </CardBody>
                  </Card>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    colorScheme="orange"
                    size="lg"
                    borderRadius="xl"
                    isLoading={isLoading}
                    loadingText="Submitting Request..."
                    isDisabled={!formData.email || !formData.phoneNumber || !formData.password || formData.dataTypes.length === 0 || !formData.confirmDeletion}
                    leftIcon={<Database size={20} />}
                  >
                    Request Data Deletion
                  </Button>
                </VStack>
              </form>

              {/* Additional Information */}
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack spacing={3} align="start">
                    <Text fontWeight="semibold" color="gray.800">What happens next?</Text>
                    <VStack spacing={2} align="start" fontSize="sm" color="gray.600">
                      <Text>• You'll receive a confirmation email within 24 hours</Text>
                      <Text>• Selected data will be marked for deletion</Text>
                      <Text>• Data will be permanently deleted after 30 days</Text>
                      <Text>• Your account will remain active with remaining data</Text>
                      <Text>• You can contact support to cancel the deletion during the processing period</Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </CardBody>
        </Card>
      </Center>
    </Box>
  )
}
